import React from 'react';
import { BarChart3, Check, Cpu, Headphones, LifeBuoy, Megaphone, MessageSquare, Receipt, Sparkles, TrendingUp, Users } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { PixelCursor } from '../alt/PixelCursor';

/**
 * THE ARTEFACTS ON THE AI AGENTS PAGE ARE NOT SCREENSHOTS.
 *
 * They are idealised product objects: the same components the real UI uses,
 * but with larger type, fewer elements and no chrome, so each one is legible
 * at a third of a screen and reads as ONE designed thing rather than a photo
 * of a window. A literal screenshot shrunk to this size loses its own labels,
 * which is the failure mode this file exists to avoid.
 *
 * They all share the site's surface language: white fill, the warm #E7E0D4
 * hairline, a 22px radius on the outer objects, and the two-part shadow used
 * by every card on the home page.
 */

export const CARD_SHADOW = 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]';

/**
 * Category chips. Colour is FUNCTIONAL here, not decorative — it is how an
 * operator scans a directory of agents for the ones that belong to their
 * department, so this is the one place on the site where a second and third
 * hue are earned.
 *
 * They were originally held at very low saturation, which defeated the point:
 * six chips within a few percent of each other in both hue and chroma cannot
 * be told apart at a glance, so they stopped being a scanning aid and just
 * added grey noise to every card. Each ground is now a clear, distinct tint
 * and each label is a saturated version of the same hue, held dark enough to
 * clear 4.5:1 on its own ground. Sales stays the brand orange, since sales is
 * the department the page's own examples lead with.
 */
const CATEGORIES = {
  sales: {
    label: 'Sales',
    icon: TrendingUp,
    className: 'bg-[#FFE6D2] text-[#B03E06]'
  },
  marketing: {
    label: 'Marketing',
    icon: Megaphone,
    className: 'bg-[#D6F2E0] text-[#0F6B45]'
  },
  support: {
    label: 'Support',
    icon: LifeBuoy,
    className: 'bg-[#DAEAFC] text-[#14549C]'
  },
  ops: {
    label: 'Operations',
    icon: BarChart3,
    className: 'bg-[#FCEAC4] text-[#8A5A05]'
  },
  people: {
    label: 'People',
    icon: Users,
    className: 'bg-[#E8DFFB] text-[#5732B8]'
  },
  finance: {
    label: 'Finance',
    icon: Receipt,
    className: 'bg-[#D2F0EC] text-[#0B6961]'
  }
} as const;
export type AgentCategory = keyof typeof CATEGORIES;
export type AgentTemplate = {
  category: AgentCategory;
  name: string;
  blurb: string;
  /** Catalog slugs — resolved through the shared logo chain. */
  tools: [string, string][];
};

/**
 * Real recurring responsibilities rather than demo filler: each is a job a
 * person currently does by hand on a schedule, which is the whole argument for
 * giving it an owner.
 */
export const AGENT_TEMPLATES: AgentTemplate[] = [{
  category: 'sales',
  name: 'Renewal brief agent',
  blurb: 'Builds a brief before every renewal call from the CRM, the email thread and past notes.',
  tools: [['salesforce', 'Salesforce'], ['gmail', 'Gmail']]
}, {
  category: 'marketing',
  name: 'Campaign recap agent',
  blurb: 'Posts weekly performance with the top movers and what changed since last week.',
  tools: [['hubspot', 'HubSpot'], ['googleanalytics', 'Analytics']]
}, {
  category: 'support',
  name: 'Escalation watcher',
  blurb: 'Flags conversations going quiet and files the ticket with the full context attached.',
  tools: [['intercom', 'Intercom'], ['linear', 'Linear']]
}, {
  category: 'ops',
  name: 'Monday recap agent',
  blurb: "Reads the week's meetings and posts what moved, what slipped and who is waiting.",
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'people',
  name: 'Onboarding guide',
  blurb: 'Walks new starters through setup and answers from the handbook as they go.',
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'finance',
  name: 'Invoice chaser',
  blurb: 'Chases unpaid invoices on a schedule and logs every reply against the account.',
  tools: [['xero', 'Xero'], ['gmail', 'Gmail']]
}];

/** The chip. */
export function CategoryChip({
  category
}: {
  category: AgentCategory;
}) {
  const meta = CATEGORIES[category];
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${meta.className}`} data-id="element-186" data-mp="21agsx5eyu5h8">
      <Icon size={13} strokeWidth={2.4} data-id="element-187" data-mp="268s3xjgzym11" />
      {meta.label}
    </span>;
}

/**
 * ONE AGENT, as it appears in the directory. This is the page's atomic unit —
 * it is the hero overlay, it tiles into the template wall, and it is what the
 * sharing panel lists. Using the same object in all three places is what makes
 * the page read as one product rather than five illustrations.
 */
export function AgentCard({
  template,
  className = ''
}: {
  template: AgentTemplate;
  className?: string;
}) {
  return <div className={`rounded-[18px] border border-[#EFE7DA] bg-white p-5 md:p-6 ${className}`} data-id="element-188" data-mp="pvjzzkfnuw4l">
      <CategoryChip category={template.category} data-id="element-189" data-mp="id3qq9blyoop" />
      <p className="font-title mt-3.5 text-[19px] font-bold tracking-[-0.01em] text-[#2B211A] md:text-[22px]" data-id="element-190" data-mp="dl2gesy0381b">
        {template.name}
      </p>
      <p className="mt-2 text-[14.5px] leading-relaxed text-[#6B5D52] md:text-[15.5px]" data-id="element-191" data-mp="8m92x0iprh15">{template.blurb}</p>
      <div className="mt-4 flex items-center gap-2" data-id="element-192" data-mp="4lz01wq7x6qi">
        {template.tools.map(([slug, name]) => <span key={slug} className="flex size-8 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-193" data-mp="119v98gjbszrp">
            <IntegrationLogo slug={slug} name={name} className="size-[17px]" rounded="rounded" data-id="element-194" data-mp="w9haxq7j146v" />
          </span>)}
      </div>
    </div>;
}

/**
 * THE TEMPLATE WALL — a spotlight onto a larger directory.
 *
 * There are MORE of these than fit, and the artefact's whole job is to say so.
 * The earlier version said it with a hard crop inside a bordered, rounded
 * frame — but a frame ANSWERS the question: cards stop because the box stops,
 * so it reads as a picture of six cards rather than a window onto hundreds.
 *
 * So there is no frame. The grid is oversized and bled past its column on
 * BOTH sides, then a single radial mask dissolves it into the section's own
 * ground. Nothing terminates the artwork except light falling off, which is
 * what makes the eye assume the wall continues.
 *
 * Two deliberate details:
 *   · The lit region sits high and slightly left of centre, over the middle
 *     card. A centred spotlight reads as a vignette effect; an off-centre one
 *     reads as a camera pointed at something.
 *   · The card under that light is the only one at full weight. Its neighbours
 *     sit back, so a dense grid has one thing to actually look at.
 *
 * The mask is a real mask rather than an overlay: these cards are not
 * interactive, and a scrim painted on top would have to match the ground
 * colour exactly or read as haze sitting over the page.
 */

/** Off-centre, high. Shared by the mask and the board so they fall off together. */
const WALL_LIGHT = 'radial-gradient(118% 95% at 46% 32%, #000 0%, #000 36%, rgba(0,0,0,0.74) 57%, rgba(0,0,0,0.3) 76%, transparent 90%)';
export function TemplateWall() {
  // Bled hard to the OUTER edge and only slightly inward: this artefact sits
  // in the right-hand half of its beat, so a symmetric bleed would push cards
  // under the copy on its left.
  // Two full rows, wide cards. Three narrow columns in a half-width column
  // made a portrait block; two wide ones over the same bled width give each
  // card room for its own type and land the artefact close to square.
  return <div aria-hidden="true" className="relative -ml-4 -mr-12 select-none sm:-mr-28 md:-ml-8 md:-mr-40" style={{
    maskImage: WALL_LIGHT,
    WebkitMaskImage: WALL_LIGHT
  }} data-id="element-195" data-mp="2ltbnxuc1ut39">
      {/* The board the directory is ruled on. No fill and no border — only the
       grid — so the light is the only thing defining where it ends. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
      backgroundImage: 'linear-gradient(to right, #E7DCCB 1px, transparent 1px), linear-gradient(to bottom, #E7DCCB 1px, transparent 1px)',
      backgroundSize: '72px 72px'
    }} data-id="element-196" data-mp="2pu5cmpxbgbx7" />

      {/* Three columns at every width above the smallest, so the cards stay
       large enough for their own type to be readable — the reason the
       reference composition works at all. */}
      <div className="relative grid grid-cols-2 gap-5 px-4 py-8 md:gap-6 md:px-6 md:py-10" data-id="element-197" data-mp="3ek0u7skl11n1">
        {AGENT_TEMPLATES.slice(0, 4).map((template, index) => {
        // Top-right sits under the lit region on the 2-up grid.
        const lit = index === 1;
        return <div key={template.name} className="relative" data-id="element-198" data-mp="3s9ugq48m79tl" data-mpi={index}>
              <AgentCard template={template} className={lit ? 'shadow-[0_2px_4px_rgba(43,33,26,0.06),0_26px_60px_-24px_rgba(43,33,26,0.34)]' : 'opacity-[0.72] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-18px_rgba(43,33,26,0.16)]'} data-id="element-199" data-mp="3sbff64y1vcwq" />
              {/* The pointer names WHICH card the claim is about. Sat on the
               lit card's lower-left corner, half on and half off it, so it
               reads as resting over the directory rather than as an icon
               placed inside a card. */}
              {lit && <PixelCursor className="pointer-events-none absolute -bottom-5 -left-4 h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:-bottom-6 md:-left-5 md:h-14" data-id="element-200" data-mp="2ea9hcx7fwzk1" />}
            </div>;
      })}
      </div>
    </div>;
}

/** Integrations, as toggles. Two on, the rest waiting — the state a workspace
 * is actually in on day one, which is more honest than an all-green list. */
const CONNECTIONS: [string, string, boolean][] = [['slack', 'Slack', true], ['salesforce', 'Salesforce', true], ['notion', 'Notion', false], ['gmail', 'Gmail', false], ['jira', 'Jira', false]];
export function ConnectionsCard() {
  return <div className={`w-full max-w-[380px] rounded-[22px] border border-[#EFE7DA] bg-white p-6 md:p-7 ${CARD_SHADOW}`} data-id="element-201" data-mp="1ll27x36g6pk0">
      <p className="font-title text-[20px] font-bold tracking-[-0.02em] text-[#2B211A] md:text-[23px]" data-id="element-202" data-mp="1sxrberi7tilq">Connections</p>
      <ul className="mt-4" data-id="element-203" data-mp="1nzpileuzm4x2">
        {CONNECTIONS.map(([slug, name, on]) => <li key={slug} className="flex items-center gap-3 border-b border-[#F1EAE0] py-3 last:border-0 md:py-3.5" data-id="element-204" data-mp="22nu2gm5s0mhf">
            <span aria-hidden="true" className={`flex h-[22px] w-[38px] flex-none items-center rounded-full px-[3px] transition-colors ${on ? 'justify-end bg-[#EA580C]' : 'justify-start bg-[#E7E0D4]'}`} data-id="element-205" data-mp="1xqaqjswjwlsn">
              <span className="flex size-4 items-center justify-center rounded-full bg-white" data-id="element-206" data-mp="152ocfqpp5yrj">
                {on && <Check size={10} strokeWidth={3.5} className="text-[#EA580C]" data-id="element-207" data-mp="1jzp5bxjmjzrl" />}
              </span>
            </span>
            <IntegrationLogo slug={slug} name={name} className="size-[22px]" rounded="rounded" data-id="element-208" data-mp="1f1nm9cm780g4" />
            <span className="text-[15px] font-semibold text-[#2B211A] md:text-[16px]" data-id="element-209" data-mp="2vfngc6yd4vvg">{name}</span>
          </li>)}
      </ul>
    </div>;
}

/**
 * THE APPROVAL PROMPT. The most load-bearing artefact on the page: it is the
 * only one that shows the agent NOT acting. Three actions in ascending trust —
 * decline, approve once, approve always — because the middle one is the choice
 * most people make first and it should not be the loud one.
 */
export function ApprovalCard({
  /**
   * 'glass' lays the prompt ON a photograph: a frosted panel holding a solid
   * white data sheet. The frost is what makes it read as a system prompt
   * surfacing over what the person is doing, rather than a card sitting on a
   * picture — and the data sheet stays opaque because it is the part you have
   * to actually read before you press anything.
   */
  variant = 'card'
}: {
  variant?: 'card' | 'glass';
} = {}) {
  const glass = variant === 'glass';
  return <div className={glass ? 'w-full rounded-[20px] border border-white/45 bg-white/45 p-4 shadow-[0_1px_2px_rgba(43,33,26,0.06),0_26px_60px_-22px_rgba(43,33,26,0.4)] backdrop-blur-2xl md:p-6' : `w-full max-w-[600px] rounded-[22px] border border-[#EFE7DA] bg-white p-5 md:p-7 ${CARD_SHADOW}`} data-id="element-210" data-mp="3gvt72t7fex6">
      <div className="flex items-start gap-3" data-id="element-211" data-mp="e2shw6yxrf56">
        <span className={`flex size-9 flex-none items-center justify-center rounded-xl bg-white md:size-10 ${glass ? 'shadow-[0_1px_2px_rgba(43,33,26,0.12)]' : 'border border-[#EFE7DA]'}`} data-id="element-212" data-mp="e0xsoggmt0va">
          <IntegrationLogo slug="slack" name="Slack" className="size-[19px] md:size-[21px]" rounded="rounded" data-id="element-213" data-mp="44mibw0lql3h" />
        </span>
        <p className={`font-semibold leading-snug text-[#2B211A] ${glass ? 'text-[16px] md:text-[18px]' : 'text-[15.5px] md:text-[17px]'}`} data-id="element-214" data-mp="3qkgwzijtcpwb">
          The Monday recap agent wants to post to a Slack channel
        </p>
      </div>

      <dl className={`mt-4 text-[14px] md:text-[15px] ${glass ? 'rounded-2xl bg-white p-4 md:p-5' : 'rounded-2xl bg-[#FAF8F4] p-4 md:p-5'}`} data-id="element-215" data-mp="3hkk414qe54vg">
        <div className="flex gap-4" data-id="element-216" data-mp="3cjwhav973a71">
          <dt className="w-[92px] flex-none font-semibold text-[#8A7B6D]" data-id="element-217" data-mp="37lusdtp38gnn">Channel</dt>
          <dd className="text-[#2B211A]" data-id="element-218" data-mp="2xho7717lluvv">#leadership</dd>
        </div>
        <div className="mt-3 flex gap-4" data-id="element-219" data-mp="2xhej5j645xst">
          <dt className="w-[92px] flex-none font-semibold text-[#8A7B6D]" data-id="element-220" data-mp="32g7vv9dktkrx">Message</dt>
          <dd className="leading-relaxed text-[#2B211A]" data-id="element-221" data-mp="37fu75d9ugqy3">
            Weekly recap is ready. Northwind moved to Q4, Acme is in legal review, and four
            renewals have gone quiet.
          </dd>
        </div>
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-2.5 md:mt-5" data-id="element-222" data-mp="2ery3tpprs0sc">
        <button type="button" className={`rounded-full border border-[#E4B9A2] px-4 py-2 text-[13.5px] font-bold text-[#B4470F] transition-colors md:px-5 md:py-2.5 md:text-[14.5px] ${glass ? 'bg-white/70 hover:bg-white' : 'hover:bg-[#FDF0E6]'}`} data-id="element-223" data-mp="2jpzxf4ktnii2">
          Decline
        </button>
        <span className="grow" data-id="element-224" data-mp="2thvxgl6gxiox" />
        <button type="button" className={`rounded-full px-4 py-2 text-[13.5px] font-bold text-[#2B211A] transition-colors md:px-5 md:py-2.5 md:text-[14.5px] ${glass ? 'bg-white hover:bg-white/90' : 'border border-[#E7E0D4] hover:bg-[#FAF8F4]'}`} data-id="element-225" data-mp="1w16yr0ctewjr">
          Always allow
        </button>
        <button type="button" className="rounded-full bg-[#EA580C] px-5 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-[#D14E0A] md:px-6 md:py-2.5 md:text-[14.5px]" data-id="element-226" data-mp="1kqfmvp2qafu">
          Approve
        </button>
      </div>
    </div>;
}

/** The shared directory — the same agents, now as a team's list. */
const SHARED = [{
  icon: TrendingUp,
  name: 'Renewal brief agent'
}, {
  icon: BarChart3,
  name: 'Monday recap agent'
}, {
  icon: Megaphone,
  name: 'Campaign recap agent'
}, {
  icon: Headphones,
  name: 'Escalation watcher'
}, {
  icon: Users,
  name: 'Onboarding guide'
}, {
  icon: Sparkles,
  name: 'Custom agent builder'
}];
/** The list runs off the bottom of its sheet rather than ending — the sheet is
 * a view into a directory of 24, not a card containing six. */
const SHARED_FALL = 'linear-gradient(to bottom, #000 0%, #000 76%, rgba(0,0,0,0.35) 92%, transparent 100%)';
export function SharedAgentsCard() {
  return <div className="relative pl-4 pt-4 sm:pl-8 sm:pt-6" data-id="element-227" data-mp="1biyiwkcyjvvo">
      {/* Two sheets behind, offset up and to the LEFT and very slightly turned,
       so the front panel reads as the top of a stack somebody is holding.
       Offset and rotated rather than scaled — a scaled copy reads as a shadow. */}
      <div aria-hidden="true" className="absolute left-0 top-0 hidden h-[86%] w-[92%] rounded-[26px] bg-[#F2EBE1] sm:block" data-id="element-228" data-mp="16qf2zgqssonp" />
      <div aria-hidden="true" className="absolute left-3 top-2.5 hidden h-[92%] w-[94%] -rotate-[1.4deg] rounded-[26px] border border-[#EFE7DA] bg-[#FBF6EF] shadow-[0_18px_44px_-28px_rgba(43,33,26,0.3)] sm:block" data-id="element-229" data-mp="su1omghk93dg" />

      <div className="relative" data-id="element-230" data-mp="nsvtyd4cb61a">
        {/* The tab bar is its OWN floating sheet, not a header inside the
         panel. That separation is what makes the two objects read as chrome
         over content rather than one long card. */}
        <div className={`relative z-10 flex items-center gap-7 rounded-[18px] border border-[#EFE7DA] bg-white px-5 py-3.5 ${CARD_SHADOW}`} data-id="element-231" data-mp="2i5u0clmx5ijc">
          <span className="relative pb-2.5 text-[16px] font-bold text-[#EA580C]" data-id="element-232" data-mp="2gnn0iagcjl5r">
            Shared with you
            <span className="ml-2.5 inline-flex size-7 items-center justify-center rounded-full bg-[#EA580C] align-middle text-[12.5px] font-bold text-white" data-id="element-233" data-mp="2bszhus80xkz3">
              12
            </span>
            {/* The underline runs past the label to the sheet's edge, as in a
             real tab strip. */}
            <span aria-hidden="true" className="absolute -bottom-[3px] left-0 right-0 h-[3px] rounded-full bg-[#EA580C]" data-id="element-234" data-mp="2uh4gxmdqkhrk" />
            {/* The pointer sits on the tab that is selected, which is the one
             fact this artefact exists to state: what you are looking at is
             the subset shared WITH you, not the whole library. */}
            <PixelCursor className="pointer-events-none absolute -bottom-7 right-1 h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:h-12" data-id="element-235" data-mp="3i7if7umwzcul" />
          </span>
          <span className="pb-2.5 text-[16px] font-semibold text-[#8A7B6D]" data-id="element-236" data-mp="zxvxr5f800c5">
            All
            <span className="ml-2.5 inline-flex size-7 items-center justify-center rounded-full bg-[#F1EAE0] align-middle text-[12.5px] font-bold text-[#6B5D52]" data-id="element-237" data-mp="14w5qzahdwqv4">
              24
            </span>
          </span>
        </div>

        {/* The content sheet, tucked under the tab bar. */}
        <div className={`relative -mt-3 overflow-hidden rounded-[22px] border border-[#EFE7DA] bg-white pt-7 ${CARD_SHADOW}`} data-id="element-238" data-mp="1iw87asa1nc89">
          <p className="font-title px-6 text-[27px] font-bold tracking-[-0.02em] text-[#2B211A]" data-id="element-239" data-mp="1nui2v3zokeue">
            Agent
          </p>

          <ul className="mt-2 px-6 pb-2" style={{
          maskImage: SHARED_FALL,
          WebkitMaskImage: SHARED_FALL
        }} data-id="element-240" data-mp="1smjkz6jj4zsc">
            {SHARED.map(({
            icon: Icon,
            name
          }) => <li key={name} className="flex items-center gap-4 border-b border-[#F5EFE6] py-3.5 last:border-0" data-id="element-241" data-mp="21oj547pucjzi">
                <span aria-hidden="true" className="flex size-10 flex-none items-center justify-center rounded-full bg-[#FAF8F4] text-[#8A7B6D]" data-id="element-242" data-mp="26jgbt7zneh98">
                  <Icon size={17} strokeWidth={2.2} data-id="element-243" data-mp="1cihjus6ewb5s" />
                </span>
                {/* The shared glyph sits DIRECTLY after the name, not pinned to
                 the far right. It qualifies the name, and a right-aligned
                 column of identical glyphs would read as a table column
                 carrying no information. */}
                <span className="flex min-w-0 items-center gap-2.5" data-id="element-244" data-mp="yprwajb8iy6k">
                  <span className="truncate text-[16.5px] font-semibold text-[#2B211A]" data-id="element-245" data-mp="tumcpr5b1vln">{name}</span>
                  <Users size={15} className="flex-none text-[#B6A895]" data-id="element-246" data-mp="17m0ujunh7qr4" />
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}

/**
 * THE BUILDER — two overlapping sheets. The editor states what the agent is
 * for; the settings sheet in front of it states what it is allowed to use. Two
 * objects rather than one tall form, because the overlap is what says these
 * are panels of a live tool.
 */
export function AgentBuilderCard() {
  return <div className="relative pb-10 pr-6 sm:pr-16" data-id="element-247" data-mp="5qenjac97ma6">
      <div className={`w-full max-w-[500px] rounded-[22px] border border-[#EFE7DA] bg-white p-6 md:p-7 ${CARD_SHADOW}`} data-id="element-248" data-mp="5r66nlonzrjk">
        <div className="flex items-center gap-3" data-id="element-249" data-mp="3s4ejzqbkwd2m">
          <span aria-hidden="true" className="flex size-10 flex-none items-center justify-center rounded-xl bg-[#FDF0E6] text-[#EA580C] md:size-11" data-id="element-250" data-mp="3s57jq7c4e2b9">
            <TrendingUp size={18} strokeWidth={2.3} data-id="element-251" data-mp="ooqy90itd47v" />
          </span>
          <p className="font-title text-[21px] font-bold leading-tight tracking-[-0.02em] text-[#2B211A] md:text-[24px]" data-id="element-252" data-mp="olv0bc6yufjf">
            Renewal brief agent
          </p>
        </div>

        <p className="mt-5 text-[13px] font-semibold text-[#8A7B6D]" data-id="element-253" data-mp="eo6wlwrdtxp4">Instructions</p>
        <p className="mt-2 rounded-2xl bg-[#FAF8F4] p-4 text-[14.5px] leading-relaxed text-[#2B211A] md:p-5 md:text-[15.5px]" data-id="element-254" data-mp="2ufmcrp75k0rg">
          Before every renewal call, pull the account's history, the open threads and the last
          three meetings, and write a one-page brief.
        </p>

        {/* The pointer lands on the one control that commits the whole sheet.
         Everything above it is description; this is the moment the agent
         exists, which is what "in minutes" beside it is claiming. */}
        <div className="relative mt-5" data-id="element-255" data-mp="1v848tq4fx3i0">
          <button type="button" className="w-full rounded-full bg-[#EA580C] py-3 text-[15px] font-bold text-white transition-colors hover:bg-[#D14E0A]" data-id="element-256" data-mp="1gkj3rqo08u7g">
            Create agent
          </button>
          <PixelCursor className="pointer-events-none absolute -bottom-6 left-[38%] h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:h-12" data-id="element-257" data-mp="17kmilz0tqdh5" />
        </div>
      </div>

      {/* In front and to the right, overlapping by about a third. */}
      <div className={`absolute bottom-0 right-0 w-[220px] rounded-[22px] border border-[#EFE7DA] bg-white p-5 ${CARD_SHADOW}`} data-id="element-258" data-mp="xmoy9mm350tt">
        <p className="text-[13px] font-semibold text-[#8A7B6D]" data-id="element-259" data-mp="12n4d6im0mgdw">Model</p>
        <p className="mt-1.5 flex items-center gap-2 rounded-full border border-[#EFE7DA] px-3 py-1.5 text-[13.5px] font-semibold text-[#2B211A]" data-id="element-260" data-mp="izqac1mfsoju">
          <Cpu size={14} className="text-[#8A7B6D]" data-id="element-261" data-mp="sy72v310xb80" />
          Your choice
        </p>

        <p className="mt-4 text-[13px] font-semibold text-[#8A7B6D]" data-id="element-262" data-mp="nyuhkmpswc7j">Runs</p>
        <p className="mt-1.5 flex items-center gap-2 text-[13.5px] font-semibold text-[#2B211A]" data-id="element-263" data-mp="o0nn1yoogqdy">
          <MessageSquare size={14} className="text-[#8A7B6D]" data-id="element-264" data-mp="11w9eecidvm9j" />
          On a Pulse
        </p>

        <p className="mt-4 text-[13px] font-semibold text-[#8A7B6D]" data-id="element-265" data-mp="wx6gdc0tmgb2">Connections</p>
        <div className="mt-2 flex items-center gap-1.5" data-id="element-266" data-mp="1bndq93crir05">
          {[['salesforce', 'Salesforce'], ['gmail', 'Gmail'], ['slack', 'Slack'], ['notion', 'Notion']].map(([slug, name]) => <span key={slug} className="flex size-7 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-267" data-mp="1kpvchhsf3jn5">
              <IntegrationLogo slug={slug} name={name} className="size-[15px]" rounded="rounded" data-id="element-268" data-mp="1gp32gmbq1apt" />
            </span>)}
        </div>
      </div>
    </div>;
}